import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';

/**
 * Hook to track page visits for analytics
 * Automatically sends visit data to the backend
 */
export const usePageTracking = () => {
  const location = useLocation();
  const lastTrackedPath = useRef(null);
  
  useEffect(() => {
    // Avoid duplicate tracking for the same path
    if (lastTrackedPath.current === location.pathname) {
      return;
    }
    lastTrackedPath.current = location.pathname;
    
    // Get tracking data
    const trackVisit = async () => {
      try {
        // Parse URL params for UTM tracking
        const params = new URLSearchParams(location.search);
        
        // Generate or get session ID
        let sessionId = sessionStorage.getItem('analytics_session_id');
        if (!sessionId) {
          sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          sessionStorage.setItem('analytics_session_id', sessionId);
        }
        
        const visitData = {
          page: location.pathname,
          referrer: document.referrer || null,
          utm_source: params.get('utm_source'),
          utm_medium: params.get('utm_medium'),
          utm_campaign: params.get('utm_campaign'),
          user_agent: navigator.userAgent,
          session_id: sessionId
        };
        
        // Send to backend (fire and forget - don't wait for response)
        api.post('/track/visit', visitData).catch(() => {
          // Silently fail - don't block user experience
        });
        
      } catch (error) {
        // Silently fail
        console.debug('Page tracking failed:', error);
      }
    };
    
    // Small delay to avoid tracking during initial render
    const timeoutId = setTimeout(trackVisit, 100);
    
    return () => clearTimeout(timeoutId);
  }, [location.pathname, location.search]);
};

/**
 * Component wrapper to enable page tracking
 * Add this component anywhere in your app tree to enable tracking
 */
const PageTracker = () => {
  usePageTracking();
  return null;
};

export default PageTracker;
